import React, { useState } from 'react';
import { Calendar, Clock, User, CheckCircle2, Phone, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { industries } from '../../data/mock';
import { toast } from 'sonner';
import { auditAPI } from '../../services/api';

const CALENDAR_URL = 'https://zcal.co/dibum/30min';

const BookingSection = () => {
  const [showAuditFirst, setShowAuditFirst] = useState(false);
  const [showCalendar, setShowCalendar] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    websiteUrl: '',
    industry: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, industry: value }));
  };

  const handleQuickAuditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await auditAPI.createSubmission({
        ...formData,
        businessName: formData.fullName,
        location: 'Not specified',
        challenge: 'Quick audit before booking call',
        source: 'Website'
      });
      toast.success('Audit request submitted! Now book your call.');
      setShowAuditFirst(false);
      // Scroll to calendar
      const iframe = document.getElementById('booking-iframe');
      if (iframe) {
        iframe.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookCallClick = () => {
    // Show audit form first before allowing booking
    setShowAuditFirst(true);
  };

  const scrollToBooking = () => {
    const iframe = document.getElementById('booking-iframe');
    if (iframe) {
      iframe.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <section id="booking" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800" />
      
      {/* Decorative Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.05)_1px,transparent_1px)] bg-[size:60px_60px]" />
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-pink-600/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <span className="inline-block px-4 py-2 bg-violet-500/20 text-violet-300 rounded-full text-sm font-semibold mb-4 border border-violet-500/30">
                Free Strategy Call
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Speak Directly With{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-violet-400 to-pink-400">
                  Our AI Experts
                </span>
              </h2>
              <p className="text-lg text-slate-400">
                Get personalized advice on dominating AI search results. Submit your details first, then book your free strategy call.
              </p>
            </div>

            {/* Features */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Clock, text: '30 min call' },
                { icon: User, text: 'Senior strategist' },
                { icon: Calendar, text: 'Custom roadmap' },
                { icon: Phone, text: 'No obligation' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-violet-500/20 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-violet-400" />
                  </div>
                  <span className="text-white font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* What We'll Discuss */}
            <div className="bg-slate-800/30 rounded-2xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">What We&apos;ll Discuss</h3>
              <ul className="space-y-3">
                {[
                  'Your current AI visibility score',
                  'Competitor analysis & gaps',
                  'Quick wins for your industry',
                  'Custom optimization strategy',
                  'Pricing & timeline for your goals'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleBookCallClick}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white text-lg px-8 py-6 rounded-xl shadow-xl shadow-violet-500/25 gap-2"
            >
              <Calendar className="w-5 h-5" />
              Schedule Your Free Call
            </Button>

            <p className="text-slate-500 text-sm">
              Step 1: Fill your details → Step 2: Pick a time slot
            </p>
          </div>

          {/* Calendar Embed */}
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-violet-500/20">
            <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Book Your Strategy Call
              </h3>
            </div>
            <div className="p-1">
              <iframe
                id="booking-iframe"
                src={CALENDAR_URL}
                title="Book a call with PixZent"
                className="w-full h-[550px] border-0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Audit Form Modal */}
      <Dialog open={showAuditFirst} onOpenChange={setShowAuditFirst}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Quick Details Before Booking
            </DialogTitle>
            <DialogDescription>
              Help us prepare for your call by sharing a few details. You&apos;ll be able to book right after.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickAuditSubmit} className="space-y-4 mt-4">
            <Input
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Your full name *"
              required
              className="h-11 rounded-xl"
            />
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email address *"
              required
              className="h-11 rounded-xl"
            />
            <Input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone number *"
              required
              className="h-11 rounded-xl"
            />
            <Input
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              placeholder="Website URL (optional)"
              className="h-11 rounded-xl"
            />
            <Select value={formData.industry} onValueChange={handleSelectChange}>
              <SelectTrigger className="h-11 rounded-xl">
                <SelectValue placeholder="Select your industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((industry) => (
                  <SelectItem key={industry} value={industry}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 text-white font-semibold"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continue to Book Call
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-center text-xs text-slate-500">
              We&apos;ll also send you a free visibility audit
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BookingSection;
